import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '@/components/base/StatCard';
import {
  staffMembersData,
  staffTransactions,
  type StaffMember,
  type StaffTransaction,
  type StaffRole,
  type CompensationType,
} from '@/mocks/staff';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from '@/context/ThemeContext';

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

type DateFilter = 'all' | 'today' | 'yesterday' | 'week' | 'month' | 'custom';

const roles: StaffRole[] = ['Yönetici', 'Danışma', 'Personel'];

const CURRENT_DATE = '2026-05-09';

function isInToday(dateStr: string): boolean {
  return dateStr === CURRENT_DATE;
}

function isInThisWeek(dateStr: string): boolean {
  // 2026-05-05 ~ 2026-05-09 (Salı - Cumartesi)
  return dateStr >= '2026-05-05' && dateStr <= CURRENT_DATE;
}

function isInThisMonth(dateStr: string): boolean {
  return dateStr.startsWith('2026-05');
}

export default function PersonelPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [staffList] = useState<StaffMember[]>(staffMembersData);
  const [transactionList, setTransactionList] = useState<StaffTransaction[]>(staffTransactions);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'list' | 'cari' | 'performance'>('list');
  const [selectedStaffId, setSelectedStaffId] = useState<string>('stf-001');

  // Date filter state
  const [dateFilter, setDateFilter] = useState<DateFilter>('today');
  const [customStart, setCustomStart] = useState<string>('');
  const [customEnd, setCustomEnd] = useState<string>('');
  const [showCustomRange, setShowCustomRange] = useState(false);

  // Pay modal
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', method: 'nakit' as 'nakit' | 'havale', description: '' });

  // Alacaklandır modal (debit)
  const [debitModalOpen, setDebitModalOpen] = useState(false);
  const [debitForm, setDebitForm] = useState({ amount: '', title: '', description: '' });

  const [toasts, setToasts] = useState<Toast[]>([]);

  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const [staffForm, setStaffForm] = useState<Partial<StaffMember>>({
    fullName: '', phone: '', email: '', specialty: '', role: 'Personel', salary: 0,
    address: '', joinDate: '', leaveDate: '', active: true, notes: '',
    advance: 0, leaveDays: 0, overtimeHours: 0, takesAppointments: true, compensationType: 'maaş+prim',
  });

  function showToast(message: string, type: 'success' | 'error' = 'success') {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  }

  // Transaction filter by date
  const filteredTransactionsByDate = useMemo(() => {
    return transactionList.filter(t => {
      switch (dateFilter) {
        case 'all':
          return true;
        case 'today':
          return isInToday(t.date);
        case 'yesterday':
          return t.date === '2026-05-08';
        case 'week':
          return isInThisWeek(t.date);
        case 'month':
          return isInThisMonth(t.date);
        case 'custom':
          if (!customStart || !customEnd) return true;
          return t.date >= customStart && t.date <= customEnd;
        default:
          return true;
      }
    });
  }, [transactionList, dateFilter, customStart, customEnd]);

  const filteredStaff = useMemo(() => {
    let data = [...staffList];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(s => s.fullName.toLowerCase().includes(q) || s.specialty.toLowerCase().includes(q));
    }
    if (roleFilter !== 'all') data = data.filter(s => s.role === roleFilter);
    return data;
  }, [searchQuery, roleFilter, staffList]);

  // Cari summary with date filter applied
  const cariSummary = useMemo(() => {
    const summary: Record<string, { alacak: number; borc: number; bakiye: number }> = {};
    staffList.forEach(s => { summary[s.id] = { alacak: 0, borc: 0, bakiye: 0 }; });
    filteredTransactionsByDate.forEach(t => {
      if (!summary[t.staffId]) return;
      if (t.type === 'debit') summary[t.staffId].alacak += t.amount;
      if (t.type === 'credit') summary[t.staffId].borc += t.amount;
    });
    Object.keys(summary).forEach(id => {
      summary[id].bakiye = summary[id].alacak - summary[id].borc;
    });
    return summary;
  }, [staffList, filteredTransactionsByDate]);

  const selectedStaffTransactions = useMemo(() => {
    return filteredTransactionsByDate
      .filter(t => t.staffId === selectedStaffId)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [filteredTransactionsByDate, selectedStaffId]);

  const selectedSummary = cariSummary[selectedStaffId] || { alacak: 0, borc: 0, bakiye: 0 };

  const performanceData = useMemo(() => {
    return staffList
      .filter(s => s.role === 'Personel')
      .map(s => ({
        name: s.fullName.split(' ')[0],
        alacak: cariSummary[s.id]?.alacak || 0,
        borc: cariSummary[s.id]?.borc || 0,
        bakiye: cariSummary[s.id]?.bakiye || 0,
      }));
  }, [staffList, cariSummary]);

  const chartTextColor = theme === 'dark' ? '#8A8680' : '#6B6560';
  const chartGridColor = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  function openPayModal(staffId?: string) {
    if (staffId) setSelectedStaffId(staffId);
    const bakiye = cariSummary[staffId || selectedStaffId]?.bakiye || 0;
    setPayForm({
      amount: bakiye > 0 ? String(bakiye) : '',
      method: 'nakit',
      description: 'Gün sonu prim ödemesi',
    });
    setPayModalOpen(true);
  }

  function handlePaySubmit() {
    const amount = Number(payForm.amount);
    if (!amount || amount <= 0) {
      showToast('Geçerli bir tutar girin', 'error');
      return;
    }
    const staffName = staffList.find(s => s.id === selectedStaffId)?.fullName || '';
    const newTx: StaffTransaction = {
      id: `trn-${Date.now()}`,
      staffId: selectedStaffId,
      type: 'credit',
      title: payForm.method === 'havale' ? 'Havale Ödemesi' : 'Nakit Ödemesi',
      amount,
      date: CURRENT_DATE,
      method: payForm.method,
      description: payForm.description || `${staffName} ödeme`,
    };
    setTransactionList(prev => [newTx, ...prev]);
    setPayModalOpen(false);
    showToast(`${staffName} için ₺${amount.toLocaleString('tr-TR')} ödeme kaydedildi`);
  }

  // Alacaklandır (Debit) functions
  function openDebitModal(staffId?: string) {
    if (staffId) setSelectedStaffId(staffId);
    setDebitForm({ amount: '', title: 'Manuel Prim Tanımlama', description: '' });
    setDebitModalOpen(true);
  }

  function handleDebitSubmit() {
    const amount = Number(debitForm.amount);
    if (!amount || amount <= 0) {
      showToast('Geçerli bir tutar girin', 'error');
      return;
    }
    const staffName = staffList.find(s => s.id === selectedStaffId)?.fullName || '';
    const newTx: StaffTransaction = {
      id: `trn-${Date.now()}`,
      staffId: selectedStaffId,
      type: 'debit',
      title: debitForm.title || 'Manuel Prim',
      amount,
      date: CURRENT_DATE,
      method: 'hakediş',
      description: debitForm.description || `${staffName} alacak kaydı`,
    };
    setTransactionList(prev => [newTx, ...prev]);
    setDebitModalOpen(false);
    showToast(`${staffName} için ₺${amount.toLocaleString('tr-TR')} alacak (prim) kaydedildi`, 'success');
  }

  function openNewStaffModal() {
    setEditingStaff(null);
    setStaffForm({
      fullName: '', phone: '', email: '', specialty: '', role: 'Personel', salary: 0,
      address: '', joinDate: new Date().toISOString().split('T')[0], leaveDate: '', active: true,
      notes: '', advance: 0, leaveDays: 0, overtimeHours: 0, takesAppointments: true, compensationType: 'maaş+prim',
    });
    setStaffModalOpen(true);
  }

  function openEditStaffModal(staff: StaffMember) {
    setEditingStaff(staff);
    setStaffForm({ ...staff });
    setStaffModalOpen(true);
  }

  function handleStaffSave() {
    if (!staffForm.fullName?.trim()) { showToast('Ad Soyad zorunludur', 'error'); return; }
    if (!staffForm.phone?.trim()) { showToast('Telefon numarası zorunludur', 'error'); return; }
    if (!staffForm.specialty?.trim()) { showToast('Uzmanlık alanı zorunludur', 'error'); return; }
    if (editingStaff) {
      showToast('Personel bilgileri güncellendi');
    } else {
      showToast('Yeni personel eklendi');
    }
    setStaffModalOpen(false);
  }

  const methodIcons: Record<string, string> = {
    nakit: 'ri-money-cny-circle-line',
    havale: 'ri-exchange-dollar-line',
    avans: 'ri-hand-coin-line',
    hakediş: 'ri-star-line',
  };

  const methodColors: Record<string, string> = {
    nakit: '#10B981',
    havale: '#3B82F6',
    avans: '#F59E0B',
    hakediş: '#C9A962',
  };

  const dateFilterLabels: Record<DateFilter, string> = {
    all: 'Tümü',
    today: 'Bugün',
    yesterday: 'Dün',
    week: 'Bu Hafta',
    month: 'Bu Ay',
    custom: 'Özel Aralık',
  };

  const dateFilterRangeText = useMemo(() => {
    switch (dateFilter) {
      case 'all':
        return 'Tüm Zamanlar';
      case 'today':
        return '9 May 2026';
      case 'yesterday':
        return '8 May 2026';
      case 'week':
        return '5 — 9 May 2026';
      case 'month':
        return '1 — 9 May 2026';
      case 'custom':
        if (customStart && customEnd) return `${customStart} — ${customEnd}`;
        return 'Tarih seçin';
      default:
        return '';
    }
  }, [dateFilter, customStart, customEnd]);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[100] space-y-2">
        {toasts.map(t => (
          <div key={t.id} className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${t.type === 'success' ? 'bg-[#2D5F3E] text-white' : 'bg-[#DC2626] text-white'}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={t.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
            </div>
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Personel</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{filteredStaff.length} personel kaydı</p>
        </div>
        <button onClick={openNewStaffModal} className="spa-btn-primary text-sm flex items-center gap-2 self-start sm:self-auto whitespace-nowrap cursor-pointer">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
          Yeni Personel
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1">
        {[
          { id: 'list', label: 'Personel Listesi' },
          { id: 'cari', label: 'Cari Hesap' },
          { id: 'performance', label: 'Prim Performansı' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={`px-4 py-2 rounded-spa-sm text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${activeTab === tab.id ? 'text-white' : ''}`}
            style={activeTab === tab.id ? { background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' } : { background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Date Filter Bar */}
      <div className="spa-card p-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center"><i className="ri-calendar-line text-sm" style={{ color: 'var(--gold)' }} /></div>
          <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Dönem:</span>
        </div>
        <div className="flex gap-1 p-1 rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
          {(['all', 'today', 'yesterday', 'week', 'month', 'custom'] as DateFilter[]).map(f => (
            <button
              key={f}
              onClick={() => {
                setDateFilter(f);
                if (f === 'custom') setShowCustomRange(true);
                else setShowCustomRange(false);
              }}
              className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all"
              style={{
                background: dateFilter === f
                  ? 'linear-gradient(135deg, var(--gold), var(--gold-dark))'
                  : 'transparent',
                color: dateFilter === f ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {dateFilterLabels[f]}
            </button>
          ))}
        </div>
        <span className="text-[10px] ml-auto" style={{ color: 'var(--text-muted)' }}>
          {dateFilterRangeText}
        </span>
      </div>

      {/* Custom Range Picker */}
      {showCustomRange && (
        <div className="spa-card p-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Başlangıç:</span>
          <input
            type="date"
            className="spa-input text-sm py-1.5"
            value={customStart}
            onChange={e => setCustomStart(e.target.value)}
          />
          <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Bitiş:</span>
          <input
            type="date"
            className="spa-input text-sm py-1.5"
            value={customEnd}
            onChange={e => setCustomEnd(e.target.value)}
          />
          <button
            onClick={() => { if (!customStart || !customEnd) { showToast('Lütfen başlangıç ve bitiş tarihi seçin', 'error'); return; } setShowCustomRange(false); }}
            className="px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))', color: '#fff' }}
          >
            Uygula
          </button>
        </div>
      )}

      {/* === PERSONEL LISTESI === */}
      {activeTab === 'list' && (
        <>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
              <input className="spa-input pl-10" placeholder="İsim veya uzmanlık ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            <select className="spa-input text-sm min-w-[120px]" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
              <option value="all">Tüm Yetkiler</option>
              <option value="Yönetici">Yönetici</option>
              <option value="Danışma">Danışma</option>
              <option value="Personel">Personel</option>
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {filteredStaff.map(staff => {
              const cari = cariSummary[staff.id] || { alacak: 0, borc: 0, bakiye: 0 };
              const isPositive = cari.bakiye > 0;
              const isZero = cari.bakiye === 0;
              return (
                <div key={staff.id} className="spa-card p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0"
                        style={{ background: staff.role === 'Yönetici' ? '#8B5CF6' : staff.role === 'Danışma' ? '#3B82F6' : 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>
                        {staff.fullName.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{staff.fullName}</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{staff.specialty}</p>
                      </div>
                    </div>
                    <StatusBadge status={staff.active ? 'Aktif' : 'Pasif'}>{staff.active ? 'Aktif' : 'Pasif'}</StatusBadge>
                  </div>

                  {/* Cari özet */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 rounded-xl" style={{ background: 'var(--bg-tertiary)' }}>
                      <p className="text-xs font-bold" style={{ color: '#10B981' }}>₺{cari.alacak.toLocaleString('tr-TR')}</p>
                      <p className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Alacak</p>
                    </div>
                    <div className="p-2 rounded-xl" style={{ background: 'var(--bg-tertiary)' }}>
                      <p className="text-xs font-bold" style={{ color: '#EF4444' }}>₺{cari.borc.toLocaleString('tr-TR')}</p>
                      <p className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted)' }}>Borç</p>
                    </div>
                    <div className="p-2 rounded-xl" style={{ background: isZero ? 'var(--bg-tertiary)' : isPositive ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)' }}>
                      <p className="text-xs font-bold" style={{ color: isZero ? 'var(--text-muted)' : isPositive ? '#10B981' : '#EF4444' }}>
                        ₺{Math.abs(cari.bakiye).toLocaleString('tr-TR')}
                      </p>
                      <p className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        {isZero ? 'Bakiye' : isPositive ? 'Ödeme Yap' : 'Alacaklı'}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className="spa-badge text-[10px]" style={{ background: staff.role === 'Yönetici' ? 'rgba(139,92,246,0.12)' : staff.role === 'Danışma' ? 'rgba(59,130,246,0.12)' : 'rgba(16,185,129,0.12)', color: staff.role === 'Yönetici' ? '#8B5CF6' : staff.role === 'Danışma' ? '#3B82F6' : '#10B981' }}>{staff.role}</span>
                    <span className="spa-badge text-[10px]" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>₺{staff.salary.toLocaleString('tr-TR')}</span>
                    <span className="spa-badge text-[10px]" style={{ background: staff.compensationType === 'maaş' ? 'rgba(59,130,246,0.12)' : staff.compensationType === 'prim' ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.12)', color: staff.compensationType === 'maaş' ? '#3B82F6' : staff.compensationType === 'prim' ? '#F59E0B' : '#10B981' }}>
                      {staff.compensationType === 'maaş' ? 'Maaş' : staff.compensationType === 'prim' ? 'Prim' : 'Maaş+Prim'}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <button onClick={() => { setSelectedStaffId(staff.id); setActiveTab('cari'); }} className="flex-1 spa-btn-secondary text-xs py-2 cursor-pointer whitespace-nowrap">Cari Detay</button>
                    <button onClick={() => openDebitModal(staff.id)} className="flex-1 spa-btn-secondary text-xs py-2 cursor-pointer whitespace-nowrap" style={{ background: 'rgba(16,185,129,0.10)', color: '#10B981', border: '1px solid rgba(16,185,129,0.25)' }}>Alacaklandır</button>
                    {isPositive && (
                      <button onClick={() => openPayModal(staff.id)} className="flex-1 spa-btn-primary text-xs py-2 cursor-pointer whitespace-nowrap" style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>Ödeme Yap</button>
                    )}
                    <button onClick={() => openEditStaffModal(staff)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-theme-hover transition-colors cursor-pointer" title="Düzenle">
                      <i className="ri-pencil-line text-sm" style={{ color: 'var(--text-muted)' }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* === CARI HESAP === */}
      {activeTab === 'cari' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Sol: Personel listesi */}
          <div className="spa-card p-4 space-y-2 lg:max-h-[600px] lg:overflow-y-auto">
            <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Personeller</h3>
            {filteredStaff.map(staff => {
              const cari = cariSummary[staff.id] || { alacak: 0, borc: 0, bakiye: 0 };
              const isSelected = selectedStaffId === staff.id;
              const isPositive = cari.bakiye > 0;
              return (
                <button
                  key={staff.id}
                  onClick={() => setSelectedStaffId(staff.id)}
                  className="w-full text-left flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer"
                  style={{
                    background: isSelected ? 'rgba(201,169,98,0.10)' : 'var(--bg-tertiary)',
                    border: isSelected ? '1px solid var(--gold)' : '1px solid transparent',
                  }}
                >
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>
                    {staff.fullName.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{staff.fullName}</p>
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{staff.specialty}</p>
                  </div>
                  <span
                    className="text-[11px] font-semibold flex-shrink-0"
                    style={{ color: isPositive ? '#10B981' : '#EF4444' }}
                  >
                    ₺{Math.abs(cari.bakiye).toLocaleString('tr-TR')}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Sağ: Cari detay */}
          <div className="lg:col-span-2 space-y-4">
            {/* Özet kartları */}
            <div className="grid grid-cols-3 gap-3">
              <div className="spa-card p-4 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full" style={{ background: 'rgba(16,185,129,0.10)' }}>
                  <i className="ri-arrow-down-circle-line text-lg" style={{ color: '#10B981' }} />
                </div>
                <p className="text-lg font-bold" style={{ color: '#10B981' }}>₺{selectedSummary.alacak.toLocaleString('tr-TR')}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Toplam Alacak (Prim)</p>
              </div>
              <div className="spa-card p-4 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full" style={{ background: 'rgba(239,68,68,0.10)' }}>
                  <i className="ri-arrow-up-circle-line text-lg" style={{ color: '#EF4444' }} />
                </div>
                <p className="text-lg font-bold" style={{ color: '#EF4444' }}>₺{selectedSummary.borc.toLocaleString('tr-TR')}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Toplam Borç (Ödeme)</p>
              </div>
              <div className="spa-card p-4 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full" style={{ background: selectedSummary.bakiye >= 0 ? 'rgba(201,169,98,0.10)' : 'rgba(59,130,246,0.10)' }}>
                  <i className={`${selectedSummary.bakiye >= 0 ? 'ri-wallet-3-line' : 'ri-refund-2-line'} text-lg`} style={{ color: selectedSummary.bakiye >= 0 ? '#C9A962' : '#3B82F6' }} />
                </div>
                <p className="text-lg font-bold" style={{ color: selectedSummary.bakiye >= 0 ? '#C9A962' : '#3B82F6' }}>
                  ₺{Math.abs(selectedSummary.bakiye).toLocaleString('tr-TR')}
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  {selectedSummary.bakiye >= 0 ? 'Ödeme Yapılmalı' : 'Personel Alacaklı'}
                </p>
              </div>
            </div>

            {/* Hareket tablosu */}
            <div className="spa-card overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Cari Hareketler
                  <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                    — {staffList.find(s => s.id === selectedStaffId)?.fullName || ''}
                  </span>
                </h3>
                <div className="flex gap-2">
                  <button onClick={() => openDebitModal()} className="text-xs font-medium px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap" style={{ background: 'rgba(16,185,129,0.10)', color: '#10B981', border: '1px solid rgba(16,185,129,0.25)' }}>Alacaklandır</button>
                  <button onClick={() => openPayModal()} className="spa-btn-primary text-xs px-3 py-2 cursor-pointer whitespace-nowrap">Ödeme Yap</button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: 'var(--bg-tertiary)' }}>
                      {['Tarih', 'Hareket', 'Tür', 'Tutar', 'Ödeme', 'Açıklama'].map(h => (
                        <th key={h} className="px-3 py-3 text-left text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStaffTransactions.map(t => (
                      <tr key={t.id} className="border-b hover:bg-theme-hover transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                        <td className="px-3 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-secondary)' }}>{t.date}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: methodColors[t.method] + '15' }}>
                              <i className={`${methodIcons[t.method]} text-sm`} style={{ color: methodColors[t.method] }} />
                            </div>
                            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{t.title}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                            style={{
                              background: t.type === 'debit' ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
                              color: t.type === 'debit' ? '#10B981' : '#EF4444',
                            }}
                          >
                            {t.type === 'debit' ? 'Alacak' : 'Borç'}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap font-semibold" style={{ color: t.type === 'debit' ? '#10B981' : '#EF4444' }}>
                          {t.type === 'debit' ? '+' : '-'}₺{t.amount.toLocaleString('tr-TR')}
                        </td>
                        <td className="px-3 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                          {t.method === 'nakit' ? 'Nakit' : t.method === 'havale' ? 'Havale' : t.method === 'avans' ? 'Avans' : 'Hakediş'}
                        </td>
                        <td className="px-3 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{t.description}</td>
                      </tr>
                    ))}
                    {selectedStaffTransactions.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-8 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                          Seçili dönemde hareket kaydı bulunmuyor
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === PRIM PERFORMANSI === */}
      {activeTab === 'performance' && (
        <div className="space-y-4">
          <div className="spa-card p-4 md:p-5">
            <h3 className="text-base font-semibold font-display mb-4" style={{ color: 'var(--text-primary)' }}>Personel Performansı - Prim Dağılımı</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceData} layout="vertical" barSize={18}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} horizontal={false} />
                  <XAxis type="number" tick={{ fill: chartTextColor, fontSize: 12 }} axisLine={{ stroke: chartGridColor }} tickFormatter={(v: number) => `₺${v}`} />
                  <YAxis type="category" dataKey="name" tick={{ fill: chartTextColor, fontSize: 12 }} axisLine={{ stroke: chartGridColor }} width={70} />
                  <Tooltip contentStyle={{ background: theme === 'dark' ? '#1A1A20' : '#FFFFFF', border: `1px solid ${chartGridColor}`, borderRadius: '8px', color: theme === 'dark' ? '#E8E4DC' : '#1A1A1A' }} formatter={(value: number, name: string) => [`₺${value.toLocaleString('tr-TR')}`, name === 'alacak' ? 'Alacak' : name === 'borc' ? 'Borç' : 'Bakiye']} />
                  <Bar dataKey="alacak" name="Alacak" fill="#10B981" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="borc" name="Borç" fill="#EF4444" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="spa-card overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Personel Cari Özeti</h3>
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{dateFilterRangeText}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)' }}>
                    {['Personel', 'Alacak', 'Borç', 'Bakiye', 'Durum', 'İşlem'].map(h => (
                      <th key={h} className="px-3 py-3 text-left text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {staffList.filter(s => s.role === 'Personel').map(staff => {
                    const cari = cariSummary[staff.id] || { alacak: 0, borc: 0, bakiye: 0 };
                    const isPositive = cari.bakiye > 0;
                    const isZero = cari.bakiye === 0;
                    return (
                      <tr key={staff.id} className="border-b hover:bg-theme-hover transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                        <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>{staff.fullName}</td>
                        <td className="px-3 py-3 whitespace-nowrap font-medium" style={{ color: '#10B981' }}>₺{cari.alacak.toLocaleString('tr-TR')}</td>
                        <td className="px-3 py-3 whitespace-nowrap font-medium" style={{ color: '#EF4444' }}>₺{cari.borc.toLocaleString('tr-TR')}</td>
                        <td className="px-3 py-3 whitespace-nowrap font-semibold" style={{ color: isZero ? 'var(--text-muted)' : isPositive ? '#10B981' : '#EF4444' }}>
                          ₺{Math.abs(cari.bakiye).toLocaleString('tr-TR')}
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                            style={{
                              background: isZero ? 'rgba(107,101,96,0.10)' : isPositive ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
                              color: isZero ? '#6B6560' : isPositive ? '#10B981' : '#EF4444',
                            }}
                          >
                            {isZero ? 'Kapanmış' : isPositive ? 'Ödeme Bekliyor' : 'Alacaklı'}
                          </span>
                        </td>
                        <td className="px-3 py-3 flex gap-2">
                          <button
                            onClick={() => openDebitModal(staff.id)}
                            className="text-[10px] font-medium px-2 py-1 rounded-md cursor-pointer"
                            style={{ background: 'rgba(16,185,129,0.10)', color: '#10B981', border: '1px solid rgba(16,185,129,0.20)' }}
                          >
                            Alacaklandır
                          </button>
                          {isPositive && (
                            <button
                              onClick={() => openPayModal(staff.id)}
                              className="text-[10px] font-medium px-2 py-1 rounded-md cursor-pointer"
                              style={{ background: 'rgba(201,169,98,0.12)', color: 'var(--gold)' }}
                            >
                              Ödeme Yap
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* === ODEME MODAL (Credit) === */}
      {payModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={e => { if (e.target === e.currentTarget) setPayModalOpen(false); }}>
          <div className="spa-card w-full max-w-md flex flex-col p-5 md:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>
                Ödeme Yap
              </h2>
              <button onClick={() => setPayModalOpen(false)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-theme-hover transition-colors cursor-pointer">
                <i className="ri-close-line text-sm" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            <div className="p-3 rounded-xl flex items-center gap-3" style={{ background: 'var(--bg-tertiary)' }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>
                {staffList.find(s => s.id === selectedStaffId)?.fullName.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{staffList.find(s => s.id === selectedStaffId)?.fullName}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Mevcut Bakiye: <span className="font-semibold" style={{ color: selectedSummary.bakiye > 0 ? '#10B981' : '#EF4444' }}>₺{Math.abs(selectedSummary.bakiye).toLocaleString('tr-TR')}</span>
                </p>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Ödeme Tutarı (₺)</label>
              <input
                type="number"
                className="spa-input w-full"
                value={payForm.amount}
                onChange={e => setPayForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0"
              />
            </div>

            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Ödeme Yöntemi</label>
              <div className="flex gap-2">
                {[
                  { key: 'nakit', label: 'Nakit', icon: 'ri-money-cny-circle-line' },
                  { key: 'havale', label: 'Havale', icon: 'ri-exchange-dollar-line' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setPayForm(prev => ({ ...prev, method: opt.key as 'nakit' | 'havale' }))}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all"
                    style={{
                      background: payForm.method === opt.key ? 'rgba(201,169,98,0.12)' : 'var(--bg-tertiary)',
                      color: payForm.method === opt.key ? 'var(--gold)' : 'var(--text-secondary)',
                      border: payForm.method === opt.key ? '1px solid var(--gold)' : '1px solid var(--border-color)',
                    }}
                  >
                    <i className={opt.icon} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Açıklama</label>
              <input
                className="spa-input w-full"
                value={payForm.description}
                onChange={e => setPayForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ödeme açıklaması..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setPayModalOpen(false)} className="spa-btn-outline text-sm whitespace-nowrap cursor-pointer">İptal</button>
              <button onClick={handlePaySubmit} className="spa-btn-primary text-sm whitespace-nowrap cursor-pointer">Ödeme Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* === ALACAKLANDIR MODAL (Debit) === */}
      {debitModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={e => { if (e.target === e.currentTarget) setDebitModalOpen(false); }}>
          <div className="spa-card w-full max-w-md flex flex-col p-5 md:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>
                Alacaklandır (Prim Tanımla)
              </h2>
              <button onClick={() => setDebitModalOpen(false)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-theme-hover transition-colors cursor-pointer">
                <i className="ri-close-line text-sm" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            <div className="p-3 rounded-xl flex items-center gap-3" style={{ background: 'var(--bg-tertiary)' }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>
                {staffList.find(s => s.id === selectedStaffId)?.fullName.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{staffList.find(s => s.id === selectedStaffId)?.fullName}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Mevcut Bakiye: <span className="font-semibold" style={{ color: selectedSummary.bakiye > 0 ? '#10B981' : '#EF4444' }}>₺{Math.abs(selectedSummary.bakiye).toLocaleString('tr-TR')}</span>
                </p>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Alacak Tutarı (₺)</label>
              <input
                type="number"
                className="spa-input w-full"
                value={debitForm.amount}
                onChange={e => setDebitForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0"
              />
              <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Personel için manuel prim veya ek kazanç tanımlayın</p>
            </div>

            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Hareket Adı</label>
              <input
                className="spa-input w-full"
                value={debitForm.title}
                onChange={e => setDebitForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Örn: Hafta Sonu Prim, Ek Mesai, Bonus..."
              />
            </div>

            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Açıklama</label>
              <input
                className="spa-input w-full"
                value={debitForm.description}
                onChange={e => setDebitForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Alacak açıklaması..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setDebitModalOpen(false)} className="spa-btn-outline text-sm whitespace-nowrap cursor-pointer">İptal</button>
              <button onClick={handleDebitSubmit} className="text-sm whitespace-nowrap cursor-pointer px-4 py-2 rounded-lg font-medium" style={{ background: '#10B981', color: '#fff' }}>Alacak Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* === PERSONEL MODAL === */}
      {staffModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto" onClick={e => { if (e.target === e.currentTarget) setStaffModalOpen(false); }}>
          <div className="spa-card w-full max-w-lg flex flex-col max-h-[90vh] p-5 md:p-6 space-y-4">
            <div className="flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>
                {editingStaff ? 'Personel Düzenle' : 'Yeni Personel'}
              </h2>
              <button onClick={() => setStaffModalOpen(false)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-theme-hover transition-colors cursor-pointer">
                <i className="ri-close-line text-sm" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
            <div className="space-y-4 overflow-y-auto flex-1 min-h-0">
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Ad Soyad <span style={{ color: '#DC2626' }}>*</span></label>
                <input className="spa-input w-full" value={staffForm.fullName || ''} onChange={e => setStaffForm(prev => ({ ...prev, fullName: e.target.value }))} placeholder="Örn: Ayşe Korkmaz" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Telefon <span style={{ color: '#DC2626' }}>*</span></label>
                  <input className="spa-input w-full" value={staffForm.phone || ''} onChange={e => setStaffForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="+90 5XX XXX XXXX" />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>E-posta</label>
                  <input type="email" className="spa-input w-full" value={staffForm.email || ''} onChange={e => setStaffForm(prev => ({ ...prev, email: e.target.value }))} placeholder="ornek@email.com" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Uzmanlık Alanı <span style={{ color: '#DC2626' }}>*</span></label>
                <input className="spa-input w-full" value={staffForm.specialty || ''} onChange={e => setStaffForm(prev => ({ ...prev, specialty: e.target.value }))} placeholder="Örn: Masaj Uzmanı, Cilt Bakımı" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Rol</label>
                  <select className="spa-input w-full" value={staffForm.role} onChange={e => setStaffForm(prev => ({ ...prev, role: e.target.value as StaffRole }))}>
                    {roles.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Maaş (₺)</label>
                  <input type="number" className="spa-input w-full" value={staffForm.salary || 0} onChange={e => setStaffForm(prev => ({ ...prev, salary: Number(e.target.value) }))} placeholder="0" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>İşe Başlama Tarihi</label>
                  <input type="date" className="spa-input w-full" value={staffForm.joinDate || ''} onChange={e => setStaffForm(prev => ({ ...prev, joinDate: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>İşten Ayrılış Tarihi</label>
                  <input type="date" className="spa-input w-full" value={staffForm.leaveDate || ''} onChange={e => setStaffForm(prev => ({ ...prev, leaveDate: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-3 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={staffForm.active ?? true} onChange={e => setStaffForm(prev => ({ ...prev, active: e.target.checked }))} />
                    <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Aktif</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={staffForm.takesAppointments ?? true} onChange={e => setStaffForm(prev => ({ ...prev, takesAppointments: e.target.checked }))} />
                    <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Randevu Alır</span>
                  </label>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Çalışma Tipi</label>
                  <select className="spa-input w-full" value={staffForm.compensationType || 'maaş+prim'} onChange={e => setStaffForm(prev => ({ ...prev, compensationType: e.target.value as CompensationType }))}>
                    <option value="maaş">Maaş</option>
                    <option value="prim">Prim</option>
                    <option value="maaş+prim">Maaş + Prim</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Adres</label>
                <input className="spa-input w-full" value={staffForm.address || ''} onChange={e => setStaffForm(prev => ({ ...prev, address: e.target.value }))} placeholder="Mahalle, İlçe, İl" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Notlar</label>
                <textarea className="spa-input w-full min-h-[80px] resize-none" value={staffForm.notes || ''} onChange={e => setStaffForm(prev => ({ ...prev, notes: e.target.value.slice(0, 500) }))} placeholder="Personel hakkında notlar..." maxLength={500} />
                <p className="text-[10px] text-right mt-1" style={{ color: 'var(--text-muted)' }}>{(staffForm.notes || '').length}/500</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2 flex-shrink-0">
              <button onClick={() => setStaffModalOpen(false)} className="spa-btn-outline text-sm whitespace-nowrap cursor-pointer">İptal</button>
              <button onClick={handleStaffSave} className="spa-btn-primary text-sm whitespace-nowrap cursor-pointer">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}